import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Alert, Box, Button, Checkbox, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { MAIN_URL } from "../../Configurations/Urls";
import Layout4 from "../DataLayouts/Layout4";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import ListSummaryCards from "../../Components/List/ListSummaryCards";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function pairsToKeySet(pairs, objectKeys) {
  const s = new Set();
  (pairs || []).forEach((p) => {
    if (Array.isArray(p) && p.length >= 2) {
      s.add(`${p[0]}:${p[1]}`);
    } else if (p && typeof p === "object") {
      const a = p[objectKeys[0]];
      const b = p[objectKeys[1]];
      if (a != null && b != null) s.add(`${a}:${b}`);
    }
  });
  return s;
}

const pairKey = (appId, entityId) => `${appId}:${entityId}`;

export default function PermissionsAccess() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [applications, setApplications] = useState([]);
  const [modules, setModules] = useState([]);
  const [, setUsesModulePivot] = useState(false);
  const [modulePairKeys, setModulePairKeys] = useState(() => new Set());
  const [featurePairKeys, setFeaturePairKeys] = useState(() => new Set());
  const [initialModulePairKeys, setInitialModulePairKeys] = useState(
    () => new Set()
  );
  const [initialFeaturePairKeys, setInitialFeaturePairKeys] = useState(
    () => new Set()
  );

  const loadAssignment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/core/permission-assignment`,
        { headers: authHeaders() }
      );
      const d = res.data || {};
      setApplications(d.applications || []);
      setModules(d.modules || []);
      setUsesModulePivot(Boolean(d.uses_module_pivot));
      const loadedModuleKeys = pairsToKeySet(d.module_pairs, [
        "application_id",
        "module_id",
      ]);
      const loadedFeatureKeys = pairsToKeySet(d.feature_pairs, [
        "application_id",
        "module_feature_id",
      ]);
      const syncedModuleKeys = new Set(loadedModuleKeys);
      (d.modules || []).forEach((mod) => {
        (d.applications || []).forEach((app) => {
          const hasFeature = (mod.features || []).some((f) =>
            loadedFeatureKeys.has(pairKey(app.application_id, f.module_feature_id))
          );
          if (hasFeature) {
            syncedModuleKeys.add(pairKey(app.application_id, mod.module_id));
          }
        });
      });
      setModulePairKeys(syncedModuleKeys);
      setFeaturePairKeys(loadedFeatureKeys);
      setInitialModulePairKeys(new Set(syncedModuleKeys));
      setInitialFeaturePairKeys(new Set(loadedFeatureKeys));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load assignment data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignment();
  }, [loadAssignment]);

  const toggleModuleCell = useCallback(
    (applicationId, moduleId) => {
      const mod = modules.find((m) => m.module_id === moduleId);
      const moduleK = pairKey(applicationId, moduleId);

      setModulePairKeys((prevM) => {
        const had = prevM.has(moduleK);
        const turnFeaturesOn = !had;
        const nextM = new Set(prevM);
        if (had) nextM.delete(moduleK);
        else nextM.add(moduleK);

        setFeaturePairKeys((prevF) => {
          const nextF = new Set(prevF);
          (mod?.features || []).forEach((f) => {
            const fk = pairKey(applicationId, f.module_feature_id);
            if (turnFeaturesOn) nextF.add(fk);
            else nextF.delete(fk);
          });
          return nextF;
        });

        return nextM;
      });
    },
    [modules]
  );

  const toggleFeatureCell = useCallback(
    (applicationId, moduleFeatureId, moduleId) => {
      const k = pairKey(applicationId, moduleFeatureId);
      setFeaturePairKeys((prev) => {
        const next = new Set(prev);
        const turningOn = !next.has(k);
        if (turningOn) next.add(k);
        else next.delete(k);

        if (moduleId != null) {
          const moduleK = pairKey(applicationId, moduleId);
          const mod = modules.find((m) => m.module_id === moduleId);

          setModulePairKeys((prevM) => {
            const nextM = new Set(prevM);
            if (turningOn) {
              nextM.add(moduleK);
            } else {
              const anySiblingChecked = (mod?.features || []).some(
                (f) =>
                  f.module_feature_id !== moduleFeatureId &&
                  next.has(pairKey(applicationId, f.module_feature_id))
              );
              if (!anySiblingChecked) nextM.delete(moduleK);
            }
            return nextM;
          });
        }

        return next;
      });
    },
    [modules]
  );

  const keysToPairs = (keys, entityField) => {
    const pairs = [];
    keys.forEach((key) => {
      const [application_id, entityId] = key.split(":").map(Number);
      pairs.push({ application_id, [entityField]: entityId });
    });
    return pairs;
  };

  const diffRemovedKeys = (initialKeys, currentKeys, entityField) => {
    const removed = new Set();
    initialKeys.forEach((key) => {
      if (!currentKeys.has(key)) removed.add(key);
    });
    return keysToPairs(removed, entityField);
  };

  const diffAddedKeys = (initialKeys, currentKeys, entityField) => {
    const added = new Set();
    currentKeys.forEach((key) => {
      if (!initialKeys.has(key)) added.add(key);
    });
    return keysToPairs(added, entityField);
  };

  const buildPayload = () => {
    return {
      module_pairs: diffAddedKeys(
        initialModulePairKeys,
        modulePairKeys,
        "module_id"
      ),
      feature_pairs: diffAddedKeys(
        initialFeaturePairKeys,
        featurePairKeys,
        "module_feature_id"
      ),
      removed_module_pairs: diffRemovedKeys(
        initialModulePairKeys,
        modulePairKeys,
        "module_id"
      ),
      removed_feature_pairs: diffRemovedKeys(
        initialFeaturePairKeys,
        featurePairKeys,
        "module_feature_id"
      ),
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${MAIN_URL}/api/core/permission-assignment`,
        buildPayload(),
        { headers: authHeaders() }
      );
      toast.success("Saved.");
      setInitialModulePairKeys(new Set(modulePairKeys));
      setInitialFeaturePairKeys(new Set(featurePairKeys));
      await loadAssignment();
    } catch (err) {
      const data = err.response?.data;
      const msg =
        (typeof data?.message === "string" && data.message) ||
        err.message ||
        "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const tableRows = useMemo(() => {
    const rows = [];
    modules.forEach((mod) => {
      rows.push({
        row_id: `m-${mod.module_id}`,
        row_type: "module",
        module_id: mod.module_id,
        name: mod.module_name,
        is_module: true,
      });
      (mod.features || []).forEach((feature) => {
        rows.push({
          row_id: `f-${feature.module_feature_id}`,
          row_type: "feature",
          module_id: mod.module_id,
          module_feature_id: feature.module_feature_id,
          name: feature.module_feature_name,
          is_module: false,
        });
      });
    });
    return rows;
  }, [modules]);

  const configss = useMemo(
    () => [
      {
        field: "name",
        label: "Module / feature",
        visible: true,
        width: 280,
        filterable: true,
        sortable: true,
        pinned: "left",
        required: true,
      },
      ...applications.map((app) => ({
        field: `app_${app.application_id}`,
        label: app.application_name || `App ${app.application_id}`,
        visible: true,
        width: 140,
        filterable: false,
        sortable: false,
        pinned: "none",
        required: false,
      })),
    ],
    [applications]
  );

  const renderCell = useCallback(
    (row, col) => {
      if (col?.key === "name") {
        return (
          <Box
            sx={{
              fontWeight: row.is_module ? 700 : 400,
              pl: row.is_module ? 0 : 2,
              color: row.is_module ? "inherit" : "text.secondary",
            }}
          >
            {row.name}
          </Box>
        );
      }
      if (!String(col?.key || "").startsWith("app_")) return undefined;
      const appId = Number(String(col.key).replace("app_", ""));
      if (row.is_module) {
        return (
          <Checkbox
            size="small"
            checked={modulePairKeys.has(pairKey(appId, row.module_id))}
            onChange={() => toggleModuleCell(appId, row.module_id)}
            inputProps={{ "aria-label": `${col.label} — ${row.name}` }}
          />
        );
      }
      return (
        <Checkbox
          size="small"
          checked={featurePairKeys.has(
            pairKey(appId, row.module_feature_id)
          )}
          onChange={() =>
            toggleFeatureCell(appId, row.module_feature_id, row.module_id)
          }
          inputProps={{ "aria-label": `${col.label} — ${row.name}` }}
        />
      );
    },
    [featurePairKeys, modulePairKeys, toggleFeatureCell, toggleModuleCell]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "apps",
        label: "Applications",
        value: applications.length,
        iconKey: "apps",
      },
      {
        key: "modules",
        label: "Modules",
        value: modules.length,
        iconKey: "layers",
      },
      {
        key: "assigned-modules",
        label: "Assigned modules",
        value: modulePairKeys.size,
        iconKey: "check",
        tone: "ok",
      },
      {
        key: "assigned-features",
        label: "Assigned features",
        value: featurePairKeys.size,
        iconKey: "shield",
      },
    ],
    [applications.length, modules.length, modulePairKeys, featurePairKeys]
  );

  const saveButton = (
    <Button
      className="ems-btn ems-primary"
      variant="contained"
      startIcon={
        saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
      }
      disabled={saving || loading}
      onClick={handleSave}
    >
      Save matrix
    </Button>
  );

  if (loading && applications.length === 0 && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && applications.length === 0) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Layout4
        loading={loading}
        heading="Permissions access"
        subtitle="Rows are catalog modules and their features. Columns are applications — tick a cell to link that module or feature."
        showAddButton={false}
        headerExtra={saveButton}
      />
      <ListSummaryCards cards={summaryCards} />
      {applications.length === 0 ? (
        <Alert severity="info">No applications defined.</Alert>
      ) : (
        <TableDataGeneric
          tableName="permission_assignment"
          primaryKey="row_id"
          heading="Permissions access"
          data={tableRows}
          sortname="name"
          showActions={false}
          hideToolbar={false}
          token={localStorage.getItem("token")}
          configss={configss}
          renderCell={renderCell}
          enableRowSelection={false}
        />
      )}
    </>
  );
}
